import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTelegramPlane, FaUserTie, FaCommentDots, FaCheckCircle } from 'react-icons/fa';
import { MdSend } from 'react-icons/md';
import constantObj from '../constant/constant';
import { useSocial } from "../hooks/useSocial";


export default function ContactUs() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [showPopup, setShowPopup] = useState(false);
    const { socials, loading, error } = useSocial();
  
  const telegram = socials.find((i) => i.label?.toLowerCase() === "telegram");
const admin = socials.find((i) => i.label?.toLowerCase() === "admin");

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowPopup(true);
    setFormData({ name: '', email: '', message: '' });
    setTimeout(() => setShowPopup(false), 4000);
  };

  const inputClass = "w-full px-4 py-3.5 rounded-xl text-white text-sm outline-none transition-all duration-300";
  const inputStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(59,130,246,0.15)", color: "white" };

  return (
    <section id="contact" className="py-24 px-6 relative overflow-hidden" style={{ background: "var(--uea-dark)" }}>
      <div className="absolute top-0 left-0 w-full h-px"
        style={{ background: "linear-gradient(90deg, transparent, #3b82f6, transparent)" }} />

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-xs font-mono-code text-green-400 tracking-widest uppercase">// Get In Touch</span>
          <h2 className="font-orbitron font-black text-3xl sm:text-4xl text-white mt-4">
            Contact{" "}
            <span style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Our Team
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs text-slate-400 mb-2 font-mono-code uppercase tracking-widest">Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required
                  className={inputClass} style={inputStyle} placeholder="Your full name" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-2 font-mono-code uppercase tracking-widest">Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required
                  className={inputClass} style={inputStyle} placeholder="your@email.com" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-2 font-mono-code uppercase tracking-widest">Message</label>
                <textarea name="message" value={formData.message} onChange={handleChange} required rows="5"
                  className={inputClass} style={inputStyle} placeholder="Tell us how we can help..." />
              </div>
              <motion.a href={admin?.url || "#"}
                whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(34,197,94,0.3)" }} whileTap={{ scale: 0.98 }}
                className="flex items-center justify-center gap-2 w-full py-4 rounded-xl font-orbitron text-sm tracking-wider font-bold text-white text-center"
                style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)" }}>
                <MdSend className="text-lg" /> Send Message
              </motion.a>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="space-y-6">
            <div className="p-6 rounded-2xl border" style={{ borderColor: "rgba(34,197,94,0.2)", background: "rgba(34,197,94,0.03)" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e" }}>
                  <FaCommentDots className="text-lg" />
                </div>
                <span className="text-green-400 font-semibold text-sm font-orbitron">Support Message</span>
              </div>
              <p className="text-slate-300 italic text-base leading-relaxed">
                "Our support team is dedicated to ensuring you have the best experience. Don't hesitate to reach out!"
              </p>
              <a href={admin?.url||"#"} className="block mt-4 text-sm text-green-400 font-semibold">
                — UEA Trading Software Support Team
              </a>
            </div>

            {[
              { icon: <FaTelegramPlane className="text-lg" />, label: "Telegram", value: "Join our community channel", href: telegram?.url||"#", color: "#3b82f6" },
              { icon: <FaUserTie className="text-lg" />, label: "Direct Admin", value: "Talk to admin directly", href: admin?.url||'#', color: "#22c55e" },
            ].map((c, i) => (
              <motion.a key={i} href={c.href} whileHover={{ x: 4 }}
                className="flex items-center gap-4 p-5 rounded-xl border transition-all"
                style={{ borderColor: "rgba(59,130,246,0.15)", background: "var(--uea-card)" }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${c.color}15`, color: c.color, border: `1px solid ${c.color}30` }}>
                  {c.icon}
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{c.label}</div>
                  <div className="text-slate-400 text-xs">{c.value}</div>
                </div>
                <span className="ml-auto text-slate-500">→</span>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {showPopup && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 px-8 py-4 rounded-full text-white text-sm font-semibold z-50 flex items-center gap-2"
            style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)", boxShadow: "0 0 30px rgba(34,197,94,0.4)" }}>
            <FaCheckCircle /> Message sent! We'll be in touch soon.
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
